import { Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { OrderShipmentGroup } from 'src/app/models';
import { LineStatus, OrderScreen } from 'src/app/models/enums';
import { OrderShipment } from 'src/app/models/order-shipment';
import { OrderShipmentSelection } from 'src/app/modules/order-shipments/models';
import { OrderUtilityService } from 'src/app/services';
import { OrderComponent } from 'src/app/shared/components/order/order.component';

const DEFAULT_EXPANDED: Readonly<Record<number, boolean>> = {
  [LineStatus.Pending]: true,
  [LineStatus.Watermarking]: true,
  [LineStatus.Transcoding]: true,
  [LineStatus.Encrypting]: true,
  [LineStatus.Packaging]: true,
  [LineStatus.Authoring]: true,
  [LineStatus.PinPlay]: true,
  [LineStatus.Licensing]: true,
  [LineStatus.PendingDownload]: true,
  [LineStatus.Uploading]: true,
  [LineStatus.Burning]: true,
  [LineStatus.IngestingMetadata]: true,
  [LineStatus.PendingShipped]: true,
  [LineStatus.Complete]: false, // Completed orders should be hidden by default
};

@Component({
  selector: 'app-order-view-status',
  templateUrl: './order-view-status.component.html',
  styleUrls: ['./order-view-status.component.scss'],
  imports: [MatFormFieldModule, MatIcon, MatSelectModule, MatButtonModule, MatOption, AccordionModule, OrderComponent],
  standalone: true,
})
export class OrderViewStatusComponent {
  // == DEPENDENCIES ============================

  public orderUtilityService = inject(OrderUtilityService);

  // == INPUTS & OUTPUTS ========================

  public orderShipmentGroups = input.required<OrderShipmentGroup[]>();
  public selectedOrderEvent = output<OrderShipmentSelection>();

  // == PROPERTIES ==============================

  public orderScreens = OrderScreen;
  public lineStatuses = LineStatus;

  // == ACCORDION STATE =========================

  public readonly viewStep = signal<LineStatus>(LineStatus.All);
  public readonly expanded = signal<Record<number, boolean>>({ ...DEFAULT_EXPANDED });

  public selectViewStep(step: LineStatus): void {
    this.viewStep.set(step);
    this.expanded.update((sections) =>
      this.mapSections(sections, (status) => step === LineStatus.All || status === step),
    );
  }

  public expandAll(): void {
    this.expanded.update((sections) => this.mapSections(sections, () => true));
  }

  public collapseAll(): void {
    this.expanded.update((sections) => this.mapSections(sections, () => false));
  }

  public toggleSection(status: LineStatus, isExpanded: boolean): void {
    this.expanded.update((sections) => ({ ...sections, [status]: isExpanded }));
  }

  public isExpanded(status: LineStatus): boolean {
    return this.expanded()[status] ?? false;
  }

  private mapSections(sections: Record<number, boolean>, value: (status: number) => boolean): Record<number, boolean> {
    const next: Record<number, boolean> = {};

    for (const key of Object.keys(sections)) {
      next[key] = value(Number(key));
    }

    return next;
  }

  // == DROPDOWN OPTIONS ========================

  public readonly expandButtons = computed<LineStatus[]>(() =>
    this.orderShipmentGroups()
      .filter((group) => group.orderShipments.length > 0)
      .map((group) => group.status),
  );

  public isExpandButtonHidden(status: LineStatus): boolean {
    return !this.orderShipmentGroups().some((group) => group.status === status && group.orderShipments.length > 0);
  }

  // == ORDER SELECTION =========================

  public currentlySelectedOrderShipmentSelection = signal<OrderShipmentSelection | null>(null);

  public clickedOrderToLoad(orderShipmentSelect: OrderShipmentSelection): void {
    this.currentlySelectedOrderShipmentSelection.set(orderShipmentSelect);
    this.selectedOrderEvent.emit(orderShipmentSelect);
  }

  public isOrderCurrentlySelected(order: OrderShipment): boolean {
    const selection = this.currentlySelectedOrderShipmentSelection();

    if (!order || selection === null) {
      return false;
    }

    return order.orderNumber === selection.orderNumber && order.shipmentNumber === selection.shipmentNumber;
  }
}
